import { useMutation } from '@tanstack/react-query';
import {
  Logo,
  getCollectionLetter,
  lightTheme,
  reportError,
} from '@vgrubs/components';
// Direct file imports: the same font files the app's GlobalStyles load.
import sfProDisplayBold from 'components/src/assets/font/sfProDisplayBold.otf';
import sfProDisplayRegular from 'components/src/assets/font/sfProDisplayRegular.otf';
import { createElement } from 'react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';

/** `yyyyMMdd` of today, local time, like the letter's own reference. */
const today = () => {
  const now = new Date();
  const pad = (value: number) => `${value}`.padStart(2, '0');
  return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
};

/**
 * Fills the new tab: a bar with the file name and a Download button, the PDF
 * viewer under it. Pointing the tab at the blob URL itself would title it
 * and name the download with the blob's random id.
 */
const showPdf = (tab: Window, url: string, fileName: string) => {
  const doc = tab.document;
  // Drops the loading screen, its styles included; the title goes back after.
  doc.head.textContent = '';
  doc.body.textContent = '';
  doc.title = fileName;
  // The background is the PDF viewer's own grey, so the bar reads as part of it.
  doc.body.style.cssText =
    'margin:0;height:100vh;display:flex;flex-direction:column;font:14px sans-serif;background:#323639';

  const bar = doc.createElement('div');
  bar.style.cssText =
    'display:flex;align-items:center;justify-content:space-between;gap:12px;padding:8px 16px;color:#fff';
  const name = doc.createElement('span');
  name.textContent = fileName;
  const download = doc.createElement('a');
  download.href = url;
  download.download = fileName;
  download.textContent = 'Download';
  download.style.cssText = `color:${lightTheme.white};background:${lightTheme.primary};border-radius:16px;padding:6px 16px;text-decoration:none;font-weight:bold`;
  bar.append(name, download);

  const viewer = doc.createElement('iframe');
  viewer.src = url;
  viewer.title = fileName;
  viewer.style.cssText = 'flex:1;width:100%;border:0';

  doc.body.append(bar, viewer);
};

const FONT = "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif";

/** The red round logo as plain SVG markup, for a page React does not run in. */
const logoMarkup = () => {
  const holder = document.createElement('div');
  const root = createRoot(holder);
  flushSync(() => root.render(createElement(Logo)));
  const markup = holder.innerHTML;
  root.unmount();
  return markup;
};

/**
 * What the new tab shows while the server renders the PDF: the round logo,
 * the AdminSpinner ring and a line on why it may take a while. The tab is a
 * blank page of ours, so it is built by hand; the app's styles do not reach it.
 */
const showLoading = (tab: Window) => {
  const doc = tab.document;
  doc.title = 'Preparing the collection letter...';

  // The ring is AdminSpinner's: same size, stroke, gap and speed.
  // The app's font, loaded again: the tab is a separate document.
  const font = (file: string, weight: number) =>
    `@font-face { font-family: 'SF Pro Display'; src: url(${new URL(
      file,
      window.location.href
    )}) format('opentype'); font-weight: ${weight}; }`;
  const style = doc.createElement('style');
  style.textContent = `
    ${font(sfProDisplayRegular, 400)}
    ${font(sfProDisplayBold, 700)}
    body { margin: 0; min-height: 100vh; display: flex; align-items: center; justify-content: center; background: ${
      lightTheme.background
    }; font-family: ${FONT}; color: ${lightTheme.darkGray}; }
    .box { display: flex; flex-direction: column; align-items: center; gap: 24px; padding: 0 16px; text-align: center; }
    .logo svg { display: block; width: 88px; height: 88px; }
    .spinner { box-sizing: content-box; width: 46.98px; height: 46.98px; border: 5.22px solid ${
      lightTheme.primary
    }; border-top-color: transparent; border-radius: 50%; animation: spin 0.568s linear infinite; }
    h1 { margin: 0; font-size: 20px; }
    p { margin: -16px 0 0; font-size: 14px; opacity: 0.7; }
    @keyframes spin { to { transform: rotate(360deg); } }
  `;
  doc.head.append(style);

  const box = doc.createElement('div');
  box.className = 'box';
  const logo = doc.createElement('div');
  logo.className = 'logo';
  logo.innerHTML = logoMarkup();
  const spinner = doc.createElement('div');
  spinner.className = 'spinner';
  const title = doc.createElement('h1');
  title.textContent = 'Preparing the collection letter';
  const note = doc.createElement('p');
  note.textContent = 'A long statement can take up to a minute.';
  box.append(logo, spinner, title, note);
  doc.body.append(box);
};

const toPdfUrl = (base64: string) => {
  const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
  return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
};

/**
 * Opens a deleted location's collection letter in a new tab, in the browser's
 * PDF viewer, where it can be read and downloaded. Always a tab, never a
 * straight download: whether Chrome opened a downloaded PDF on its own
 * depended on each admin's download settings.
 *
 * The tab is opened on the click itself and pointed at the PDF once it
 * arrives - opened after the request (seconds later, no longer in the click)
 * the popup blocker would stop it. A failed request closes it again and the
 * backend's message lands in the error toast.
 *
 * The blob URL is never revoked: the tab's Download button needs it for as
 * long as the tab is open, and it goes away with the admin page.
 *
 * A mutation so it only ever runs on the click: each one renders a fresh PDF
 * on the server, which can take a while for a long statement.
 */
export const useCollectionLetter = (locationId: number) => {
  const mutation = useMutation({
    mutationFn: async (tab: Window | null) => {
      const { data, headers } = await getCollectionLetter(locationId);
      const url = toPdfUrl(data.data);
      // The letter's own reference ("VG-1321-20261022"); a backend from
      // before Oct 26, 2026 does not send it.
      const reference = headers['x-collection-letter-reference'];
      const fileName = reference
        ? `${reference}.pdf`
        : `collection-letter-${locationId}-${today()}.pdf`;
      if (tab && !tab.closed) showPdf(tab, url, fileName);
      // The popup was blocked after all: open it here, at worst blocked too.
      else window.open(url, '_blank');
    },
    onError: (error, tab) => {
      tab?.close();
      reportError(error, { context: 'collectionLetter' });
    },
  });

  const open = () => {
    if (mutation.isPending) return;
    const tab = window.open('', '_blank');
    if (tab) showLoading(tab);
    mutation.mutate(tab);
  };

  return { open, isPending: mutation.isPending };
};
